import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
	type RefObject,
} from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";

type TooltipPosition = {
	top: number;
	left: number;
	arrowLeft: number;
};

type TooltipProps = {
	isOpen: boolean;
	targetRef: RefObject<HTMLElement | null>;
	children: ReactNode;
	offset?: number;
	className?: string;
	maxWidth?: number;
	onClose: () => void;
};

const VIEWPORT_MARGIN = 8;

function Tooltip({
	isOpen,
	targetRef,
	children,
	offset = 16,
	className = "",
	maxWidth = 320,
	onClose,
}: TooltipProps) {
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const [position, setPosition] = useState<TooltipPosition | null>(null);
	const close = (e) => {
		e.preventDefault();
		e.stopPropagation();
		onClose();
	};
	useLayoutEffect(() => {
		if (!isOpen) {
			setPosition(null);
			return;
		}

		const target = targetRef.current;
		const tooltip = tooltipRef.current;
		if (!target || !tooltip) return;

		const targetRect = target.getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();
		const viewportWidth = window.innerWidth;

		const baseLeft =
			targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
		const clampedLeft = Math.max(
			VIEWPORT_MARGIN,
			Math.min(
				baseLeft,
				viewportWidth - tooltipRect.width - VIEWPORT_MARGIN
			)
		);
		const top = targetRect.bottom + offset;

		const idealArrowLeft =
			targetRect.left + targetRect.width / 2 - clampedLeft;
		const arrowLeft = Math.max(
			16,
			Math.min(idealArrowLeft, tooltipRect.width - 16)
		);

		setPosition({
			top,
			left: clampedLeft,
			arrowLeft,
		});
	}, [isOpen, offset, targetRef, children]);

	useEffect(() => {
		if (!isOpen) return;

		const updatePosition = () => {
			const target = targetRef.current;
			const tooltip = tooltipRef.current;
			if (!target || !tooltip) return;

			const targetRect = target.getBoundingClientRect();
			const tooltipRect = tooltip.getBoundingClientRect();
			const viewportWidth = window.innerWidth;

			const baseLeft =
				targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
			const clampedLeft = Math.max(
				VIEWPORT_MARGIN,
				Math.min(
					baseLeft,
					viewportWidth - tooltipRect.width - VIEWPORT_MARGIN
				)
			);
			const top = targetRect.bottom + offset;

			const idealArrowLeft =
				targetRect.left + targetRect.width / 2 - clampedLeft;
			const arrowLeft = Math.max(
				16,
				Math.min(idealArrowLeft, tooltipRect.width - 16)
			);

			setPosition({ top, left: clampedLeft, arrowLeft });
		};

		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen, offset, targetRef]);

	if (!isOpen || !targetRef.current) return null;

	return createPortal(
		<div
			ref={tooltipRef}
			role="tooltip"
			className={`fixed z-[101] bg-primary-blue p-8 text-white shadow-lg ${className}`}
			style={{
				top: position?.top ?? -9999,
				left: position?.left ?? -9999,
				maxWidth,
			}}
			onClick={() => onClose()}
		>
			<div
				className="pointer-events-none absolute -top-[10px]"
				style={{
					left: position?.arrowLeft ?? 20,
					transform: "translateX(-50%)",
				}}
			>
				<span className="block h-0 w-0 border-l-[10px] border-r-[10px] border-b-[10px] border-l-transparent border-r-transparent border-b-primary-blue" />
			</div>
			<button
				className="absolute top-1 right-1 text-white cursor-pointer"
				onClick={close}
				aria-label="Close"
				type="button"
			>
				<Icon name="cyber-close" size="2xl" />
			</button>
			{children}
		</div>,
		document.body
	);
}

export default Tooltip;
